// client/src/components/auth/ResetPasswordPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/auth/AuthProvider';
import useAlert from '../../hooks/useAlert';
import { useAuthMutations } from '../../hooks/useAuth';
import { Loader2, CheckCircle2 } from 'lucide-react';

const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const { resettoken } = useParams();
    const { setAlert } = useAlert();
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const { resetPassword, validateResetToken } = useAuthMutations();

    // 'validating' | 'valid' | 'invalid'
    const [tokenStatus, setTokenStatus] = useState('validating');
    const [userInfo, setUserInfo] = useState(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!authLoading && isAuthenticated) {
            navigate('/');
        }
    }, [isAuthenticated, authLoading, navigate]);

    useEffect(() => {
        if (!resettoken) {
            setTokenStatus('invalid');
            return;
        }

        validateResetToken.mutateAsync(resettoken)
            .then((response) => {
                setUserInfo(response.data?.data || null);
                setTokenStatus('valid');
            })
            .catch(() => {
                setTokenStatus('invalid');
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resettoken]);

    const onSubmit = async e => {
        e.preventDefault();

        if (password.length < 6) {
            setAlert('A senha deve ter pelo menos 6 caracteres.', 'danger');
            return;
        }
        if (password !== confirmPassword) {
            setAlert('As senhas não coincidem.', 'danger');
            return;
        }

        setIsSubmitting(true);
        try {
            await resetPassword.mutateAsync({ resetToken: resettoken, password });
        } catch (err) {
            // Alerta já é tratado pelo onError da mutation
        } finally {
            setIsSubmitting(false);
        }
    };

    const isLoading = authLoading || isSubmitting;

    if (tokenStatus === 'validating') {
        return (
            <div className="auth-container">
                <div className="auth-card" style={{ textAlign: 'center' }}>
                    <Loader2 className="animate-spin icon" size={32} />
                    <p style={{ marginTop: '1rem' }}>Validando link de recuperação...</p>
                </div>
            </div>
        );
    }

    if (tokenStatus === 'invalid') {
        return (
            <div className="auth-container">
                <div className="auth-card">
                    <h1>Link <span>Inválido</span></h1>
                    <p>
                        Este link de redefinição de senha é inválido ou já expirou.
                        Solicite um novo link para continuar.
                    </p>
                    <div className="auth-links">
                        <p>
                            <Link to="/forgot-password">Solicitar novo link</Link>
                        </p>
                        <p>
                            <Link to="/login">Voltar para o login</Link>
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-container">
            <div className="auth-card">
                <h1>Nova <span>Senha</span></h1>
                <p>
                    {userInfo?.name ? `Olá, ${userInfo.name}. ` : ''}
                    Defina sua nova senha de acesso.
                </p>

                <form onSubmit={onSubmit}>
                    <div className="form-group">
                        <label htmlFor="password">Nova Senha* (mínimo 6 caracteres)</label>
                        <input
                            type="password"
                            name="password"
                            id="password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            required
                            minLength={6}
                            className="form-input"
                            placeholder="••••••••"
                            autoComplete="new-password"
                            disabled={isLoading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirmar Nova Senha*</label>
                        <input
                            type="password"
                            name="confirmPassword"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            required
                            minLength={6}
                            className="form-input"
                            placeholder="••••••••"
                            autoComplete="new-password"
                            disabled={isLoading}
                        />
                    </div>

                    <div className="form-actions">
                        <button
                            type="submit"
                            className="action-button action-button-primary w-full"
                            disabled={isLoading}
                        >
                            {isLoading ? <Loader2 className="animate-spin icon" /> : <CheckCircle2 className="icon" />}
                            Redefinir Senha
                        </button>
                    </div>
                </form>

                <div className="auth-links">
                    <p>
                        Lembrou a senha?{' '}
                        <Link to="/login">Faça login</Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
